/* eslint-disable jsx-a11y/anchor-is-valid */
import clsx from 'clsx'
import { KeyboardEvent, MouseEvent, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import DateTimeComp from '../../../../components/dateComponent/DateTimeComp'
import { constraintConfig } from '../../../../constraintConfig'
import { selectAuth } from '../../../../features/authSlice'
import { useAppSelector } from '../../../../hooks'
import { toAbsoluteUrl } from '../../../helpers'
import { LoadingComponent } from '../../../../components/loadingComponent/LoadingComponent'

type Props = {
  notifications: any
  notiCountRefetch: any
  isLoading: any
}

const getExpenseTrackingId = (expense: any): string =>
  String(expense?.trackingId || expense?.trakingId || '').trim()

const expenseDetailPath = (alert: any): string => {
  const expenseId = alert?.expense?.expenseId
  if (!expenseId) return '#'
  const datePart = encodeURIComponent(alert?.createdDate || 'now')
  return `/expense-item-detail/${expenseId}/${datePart}`
}

const statusBadgeClass = (statusId?: number): string => {
  switch (statusId) {
    case constraintConfig.expenseStatus?.pendingForApproval:
      return 'badge-light-info'
    case constraintConfig.expenseStatus?.approved:
    case constraintConfig.expenseStatus?.apApproved:
    case constraintConfig.expenseStatus?.paid:
      return 'badge-light-success'
    case constraintConfig.expenseStatus?.rejected:
    case constraintConfig.expenseStatus?.RejectedAPReview:
      return 'badge-light-danger'
    default:
      return 'badge-light-primary'
  }
}

const HeaderNotificationsMenu = ({ notifications, notiCountRefetch, isLoading }: Props) => {
  const { roleID } = useAppSelector(selectAuth)
  const menuRef = useRef<HTMLDivElement>(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const closeMenu = () => {
    if (menuRef.current) {
      menuRef.current.setAttribute('data-kt-menu-dismiss', 'true')
    }
    notiCountRefetch()
  }

  const alerts = Array.isArray(notifications?.result?.notificationVM)
    ? notifications.result.notificationVM
    : []
  const teamAlerts = Array.isArray(notifications?.result?.assignManagerNotifications)
    ? notifications.result.assignManagerNotifications
    : []

  const statusOptions = useMemo(() => {
    const seen = new Map<string, string>()
    alerts.forEach((alert: any) => {
      const id = String(alert?.expenseStatus?.id ?? '')
      const label = alert?.expenseStatus?.value
      if (id && label && !seen.has(id)) {
        seen.set(id, label)
      }
    })
    return Array.from(seen.entries()).map(([value, label]) => ({ value, label }))
  }, [alerts])

  const filteredAlerts = useMemo(() => {
    const q = search.trim().toLowerCase()
    return alerts.filter((alert: any) => {
      const trackingId = getExpenseTrackingId(alert?.expense).toLowerCase()
      const title = String(alert?.expense?.expenseTitle || '').toLowerCase()
      const matchesSearch = !q || trackingId.includes(q) || title.includes(q)
      const matchesStatus = !statusFilter || String(alert?.expenseStatus?.id) === statusFilter
      return matchesSearch && matchesStatus
    })
  }, [alerts, search, statusFilter])

  const hasTeamTab = roleID === constraintConfig.roleID.role4
  const visibleTeamAlerts = hasTeamTab ? teamAlerts : []
  const totalUpdatesCount = alerts.length + visibleTeamAlerts.length

  const unreadCount = useMemo(() => {
    const unreadAlerts = alerts.filter((alert: any) => alert?.active === true).length
    const unreadTeam = visibleTeamAlerts.filter((item: any) => item?.active === true).length
    return unreadAlerts + unreadTeam
  }, [alerts, visibleTeamAlerts])

  const stopMenuClose = (e: MouseEvent | KeyboardEvent) => {
    e.stopPropagation()
  }

  const renderAlert = (alert: any, index: number) => {
    const trackingId = getExpenseTrackingId(alert?.expense)
    const title = alert?.expense?.expenseTitle || 'Untitled report'
    const expenseId = alert?.expense?.expenseId
    const updatedByName= alert?.expense?.UpdatedByName
    const to = expenseDetailPath(alert)

    return (
      <Link
        key={alert?.notificationId || index}
        to={to}
        className={`ss-notifications-item d-block text-decoration-none px-8 ${alert?.active === true ? 'ss-notifications-item--unread' : ''}`}
        onClick={(e) => {
          if (!expenseId) {
            e.preventDefault()
            return
          }
          closeMenu()
        }}
        data-tooltip-id='ss-notifications-tip'
        data-tooltip-content={expenseId ? 'Open expense detail' : 'Expense is not available'}
        aria-label={expenseId ? `Open expense detail ${trackingId || expenseId}` : 'Expense is not available'}
      >
        <div className='d-flex flex-stack py-4'>
          <div className='d-flex align-items-center min-w-0'>
            <div className='symbol symbol-35px me-4 flex-shrink-0'>
              <span className={clsx('symbol-label', `bg-light-${alert?.state || 'primary'}`)}>
                <i className='fa fa-bell fs-3'></i>
              </span>
            </div>
            <div className='mb-0 me-2 min-w-0'>
              <span className='fs-6 fw-bold d-block text-truncate ss-notifications-title'>
                {trackingId ? <span className='text-primary me-2'>{trackingId}</span> : null}
                {title}
              </span>
              <div className='fs-7 ss-notifications-meta'>
                {alert?.notificationType?.value || 'Expense'}
                <span className={`badge ${statusBadgeClass(alert?.expenseStatus?.id)} fs-7 fw-semibold ms-2`}>
                  {alert?.expenseStatus?.value || '—'}
                </span>
              </div>
            </div>
          </div>
          <span className='badge badge-light fs-8 flex-shrink-0 ms-2'>
                {trackingId ? <span className='text-primary me-2'>{updatedByName}</span> : null}
            <DateTimeComp formattedDate={alert?.createdDate} />
          </span>
        </div>
      </Link>
    )
  }

  return (
    <div
      className='menu menu-sub menu-sub-dropdown menu-column w-400px w-lg-500px ss-notifications-menu'
      data-kt-menu='true'
      ref={menuRef}
    >
      <Tooltip id='ss-notifications-tip' place='left' positionStrategy='fixed' />
      <div
        className='d-flex flex-column rounded-top position-relative overflow-hidden px-6 pt-5 pb-0'
        style={{
          background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 50%, #0f172a 100%)',
          backgroundImage: `url('${toAbsoluteUrl('/media/misc/menu-header-bg.jpg')}'), linear-gradient(135deg, #1d4ed8 0%, #1e40af 50%, #0f172a 100%)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className='d-flex align-items-center justify-content-between mb-3'>
          <div className='d-flex align-items-center'>
            <div
              className='d-flex align-items-center justify-content-center me-3 rounded-circle shadow-sm'
              style={{
                width: '38px',
                height: '38px',
                background: 'rgba(255, 255, 255, 0.18)',
                backdropFilter: 'blur(6px)',
                WebkitBackdropFilter: 'blur(6px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <i className='fa fa-bell text-white fs-4'></i>
            </div>
            <div>
              <div className='d-flex align-items-center gap-2'>
                <h4 className='text-white fw-bold mb-0 fs-5'>Notifications</h4>
                {unreadCount > 0 && (
                  <span
                    className='badge rounded-pill'
                    style={{
                      background: 'rgba(239, 68, 68, 0.9)',
                      color: '#ffffff',
                      fontSize: '0.7rem',
                      padding: '0.2rem 0.5rem',
                      fontWeight: 600,
                    }}
                  >
                    {unreadCount} new
                  </span>
                )}
              </div>
              <span className='fs-8 text-white opacity-75'>
                {totalUpdatesCount > 0
                  ? `${totalUpdatesCount} total update${totalUpdatesCount > 1 ? 's' : ''}`
                  : 'No notifications'}
              </span>
            </div>
          </div>
        </div>

        <ul className='nav nav-line-tabs nav-line-tabs-2x nav-stretch fw-semibold border-0'>
          <li className='nav-item'>
            <a
              className='nav-link text-white opacity-75 opacity-state-100 py-2 px-3 me-2 active d-inline-flex align-items-center'
              data-bs-toggle='tab'
              href='#kt_topbar_notifications_1'
              style={{ borderBottomWidth: '2px' }}
            >
              <span>Alerts</span>
              <span
                className='badge ms-2 rounded-pill'
                style={{
                  background: 'rgba(255, 255, 255, 0.22)',
                  color: '#ffffff',
                  fontSize: '0.7rem',
                  padding: '0.15rem 0.45rem',
                }}
              >
                {alerts.length}
              </span>
            </a>
          </li>
          {roleID === constraintConfig.roleID.role4 && (
            <li className='nav-item'>
              <a
                className='nav-link text-white opacity-75 opacity-state-100 py-2 px-3 d-inline-flex align-items-center'
                data-bs-toggle='tab'
                href='#kt_topbar_notifications_3'
                style={{ borderBottomWidth: '2px' }}
              >
                <span>Team Notifications</span>
                {teamAlerts.length > 0 && (
                  <span
                    className='badge ms-2 rounded-pill'
                    style={{
                      background: 'rgba(255, 255, 255, 0.22)',
                      color: '#ffffff',
                      fontSize: '0.7rem',
                      padding: '0.15rem 0.45rem',
                    }}
                  >
                    {teamAlerts.length}
                  </span>
                )}
              </a>
            </li>
          )}
        </ul>
      </div>

      <div className='tab-content'>
        <div className='tab-pane fade show active' id='kt_topbar_notifications_1' role='tabpanel'>
          <div
            className='ss-notifications-filters px-6 pt-4 pb-3'
            onMouseDown={stopMenuClose}
            onClick={stopMenuClose}
          >
            <input
              type='search'
              className='form-control form-control-sm mb-2'
              placeholder='Search tracking ID or report title'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label='Search notifications'
            />
            <select
              className='form-select form-select-sm'
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              aria-label='Filter by status'
            >
              <option value=''>All statuses</option>
              {statusOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className='mh-300px overflow-auto'>
            {isLoading ? (
              <LoadingComponent />
            ) : filteredAlerts.length > 0 ? (
              filteredAlerts.map(renderAlert)
            ) : (
              <p className='text-center my-6 px-4'>
                {alerts.length > 0 ? 'No notifications match these filters.' : 'No notifications to display.'}
              </p>
            )}
          </div>
        </div>

        <div className='tab-pane fade' id='kt_topbar_notifications_3' role='tabpanel'>
          <div className='scroll-y mh-325px my-5 px-8'>
            {teamAlerts.length > 0 ? (
              teamAlerts.map((newUser: any, index: any) => (
                <div key={`newUser${index}`} className='d-flex flex-stack py-4 w-100'>
                  <div className='d-flex justify-content-between align-items-center me-2 w-100'>
                    <div className='d-flex align-items-center'>
                      <div className='symbol symbol-35px me-4'>
                        <span className={clsx('symbol-label', `bg-light-${newUser?.state}`)}>
                          <i className='fa fa-bell fs-3'></i>
                        </span>
                      </div>
                      <Link to='/team-management' onClick={closeMenu}>
                        <div className='text-hover-primary fw-semibold ss-notifications-title'>
                          <p className='mb-0'>
                            <b className='text-capitalize'>
                              {newUser?.user?.firstName}&nbsp;
                              {newUser?.user?.middleName}&nbsp;
                              {newUser?.user?.lastName}&nbsp;
                            </b>
                            add in your team.
                          </p>
                          <span>{newUser?.user?.email}</span>
                        </div>
                      </Link>
                    </div>
                    <span className='badge badge-light fs-8 ms-auto'>
                      <DateTimeComp formattedDate={newUser?.createdDate} />
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <p className='text-center mt-5'>No notifications to display.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export { HeaderNotificationsMenu }
